import { css } from '@emotion/react';
import { palette } from '@leafygreen-ui/palette';
import { theme } from '../../theme/docsTheme';

const badgeBorderRadius = '50px';
const badgeBorderType = '1px solid';
const codeFontFamily = 'Source Code Pro';
const textFontFamily = '"Euclid Circular A", Akzidenz, "Helvetica Neue", Helvetica, Arial, sans-serif';

// Redoc's documented theme option: https://github.com/Redocly/redoc/#redoc-theme-object
// For more precise options: https://github.com/Redocly/redoc/blob/16f542bbfe856e5ae5eb40636ac79421fa2f397b/src/theme.ts
export const themeOption = {
  breakpoints: {
    small: '768px',
    medium: '1024px',
    large: '1200px',
  },
  // Code blocks in right sidebar and main content
  codeBlock: {
    backgroundColor: palette.gray.dark3,
  },
  colors: {
    error: {
      main: palette.red.base,
    },
    // Only applies to background color of badges on right sidebar/panel;
    // color and border color touched by css
    // Left sidebar have different colors. See: spanHttpCss
    http: {
      get: palette.blue.dark2,
      post: palette.green.dark3,
      put: palette.yellow.dark3,
      patch: palette.yellow.dark3,
      delete: palette.red.dark3,
    },
    // General primary theme color; used in tags and buttons like "Download" and "ApiSlackView"
    primary: {
      main: palette.black,
    },
    // Affects the colors of the responses for each endpoint in the main column,
    // and the response codes in the right sidebar.
    responses: {
      success: {
        color: palette.green.dark2,
        backgroundColor: palette.green.light3,
        tabTextColor: palette.green.base,
      },
      error: {
        color: palette.red.base,
        backgroundColor: palette.red.light3,
        tabTextColor: palette.red.base,
      },
    },
    // Main column content text color
    text: {
      primary: palette.gray.dark3,
    },
    // Example: "deprecated" badges
    warning: {
      main: palette.gray.light1,
      contrastText: palette.gray.dark1,
    },
  },
  // Right sidebar
  rightPanel: {
    backgroundColor: palette.black,
    // Endpoints
    servers: {
      overlay: {
        backgroundColor: palette.gray.dark3,
        textColor: palette.white,
      },
      url: {
        backgroundColor: palette.gray.dark3,
      },
    },
  },
  // Schema for each endpoint
  schema: {
    linesColor: palette.black,
    requireLabelColor: palette.red.base,
    // Data type
    typeNameColor: palette.gray.dark3,
  },
  // Left sidebar
  sidebar: {
    activeTextColor: palette.green.dark3,
    backgroundColor: palette.gray.light3,
    textColor: palette.black,
    width: '268px',
  },
  spacing: {
    unit: 4,
    sectionVertical: 16,
  },
  typography: {
    fontSize: theme.fontSize.default,
    fontFamily: textFontFamily,
    headings: {
      fontFamily: textFontFamily,
    },
    code: {
      backgroundColor: palette.gray.light3,
      color: palette.gray.dark3,
      fontFamily: codeFontFamily,
      fontSize: theme.fontSize.small,
    },
    links: {
      color: palette.blue.base,
      hover: palette.blue.base,
      visited: palette.blue.base,
    },
  },
};

/*
 *
 * NOTE:
 *
 * The CSS styling below consists of a combination of Redoc's named classes,
 * and classes generated by Redoc's instance of Styled Components at the current
 * version of the Redoc component. There's a risk that upgrading Redoc versions
 * will result in different classnames, especially for those related to Styled
 * Components (prefixed with "sc-").
 *
 */

const titleHeadingClass = 'sc-dFJsGO';

export const headingsCss = css`
  h1.${titleHeadingClass} {
    color: ${palette.green.dark2};
    font-family: 'MongoDB Value Serif';
    font-size: 32px;
    font-weight: 400;
    line-height: 40px;
  }

  h1.sc-fubCfw:not(.${titleHeadingClass}) {
    font-size: 24px;
    font-weight: 500;
    line-height: 32px;
  }

  h2.sc-pFZIQ {
    font-size: 18px;
    font-weight: 600;
    line-height: 24px;
  }
`;

export const codeBlockCss = css`
  // General code tags, especially for code blocks
  code {
    font-family: ${codeFontFamily};
  }

  // Code block divs
  div.hoverable {
    color: ${palette.white};
  }

  // Highlight syntax in code blocks
  // Ellipsis for nested code
  span.ellipsis:after {
    color: ${palette.white};
  }
  span.token.boolean {
    color: #ff6f44 !important;
  }
  // Keywords such as "null"
  span.token.keyword {
    color: #ff7dc3 !important;
  }
  span.token.number {
    color: #2dc4ff !important;
  }
  // Commas, periods, colons, etc.
  span.token.punctuation {
    color: ${palette.white};
  }
  // Strings
  span.token.string:not(.property) {
    color: #35de7b !important;
  }
  // Object properties that are strings, found in the main column code blocks
  span.token.string-property {
    color: #2dc4ff !important;
  }
  // Variables and options in main column code blocks
  span.token.parameter.variable {
    color: #edb210 !important;
  }
`;

export const inlineCodeCss = css`
  // InlineCode found in data types of Parameters and Schemas; example: "string 24 characters"
  span.sc-GqfZa {
    background-color: ${palette.blue.light3};
    border-color: ${palette.blue.light2};
    color: ${palette.blue.base};
  }

  // InlineCode inside of Parameters and Schemas for values, examples, and defaults
  span.sc-clsHhM {
    background-color: ${palette.gray.light3};
    border-color: ${palette.gray.light2};
    color: ${palette.gray.dark3};
  }
`;

export const leftSidebarCss = css`
  label[role='menuitem'] {
    font-size: 13px;
    font-weight: normal;

    :hover {
      background-color: ${palette.gray.light2};
    }
    &.active {
      background-color: ${palette.green.light3};
    }
  }
`;

export const rightSidebarCss = css`
  // Status code / Response tabs
  ul.react-tabs__tab-list {
    li[role='tab'] {
      background-color: ${palette.gray.dark3};
      border: unset;
      color: ${palette.gray.base} !important;
    }

    li.react-tabs__tab--selected {
      // General selected tab should have the following styles
      // Example: "Payload" button under "Request samples"
      background-color: ${palette.gray.dark2} !important;
      border: ${badgeBorderType} ${palette.gray.base};
      color: ${palette.white} !important;

      &.tab-error {
        background-color: ${palette.gray.dark2} !important;
        border: none;
        color: ${palette.red.light1} !important;
      }

      &.tab-success {
        background-color: ${palette.gray.dark2} !important;
        border: none;
        color: ${palette.green.light1} !important;
      }
    }
  }

  // Server endpoint buttons
  button.sc-eFubAy {
    background-color: ${palette.gray.dark3};
  }

  // Server endpoint overlay
  div.sc-dUrnRO {
    border-color: ${palette.gray.dark2};
    color: ${palette.white};

    > span {
      color: ${palette.white};
    }
  }

  // "Content type" box
  div.sc-bBrOnJ {
    background-color: ${palette.gray.dark2} !important;
  }

  // Container for "Copy", "Expand All", "Collapse All" code block buttons
  div.sc-giIncl > button {
    font-size: 13px;
  }
`;

export const schemaDataTypesCss = css`
  // Regex next to data types under query parameters; ex - "^([\w]{24})$"
  span.sc-gGmIRh {
    color: ${palette.gray.dark3};
  }
`;

// HTTP badges
export const spanHttpCss = css`
  // Right sidebar badges
  span.http-verb {
    border: ${badgeBorderType};
    border-radius: ${badgeBorderRadius};
    font-size: 12px;
    font-weight: bold;

    &.get {
      border-color: ${palette.blue.dark1};
      color: ${palette.blue.light2};
    }
    &.post {
      border-color: ${palette.green.dark2};
      color: ${palette.green.base};
    }
    &.patch,
    &.put {
      border-color: ${palette.yellow.dark2};
      color: ${palette.yellow.light2};
    }
    &.delete {
      border-color: ${palette.red.dark2};
      color: ${palette.red.light2};
    }
  }

  // Left sidebar badges
  span.operation-type {
    border: ${badgeBorderType};
    border-radius: ${badgeBorderRadius};
    font-family: ${textFontFamily};

    &.get {
      background-color: ${palette.blue.light3};
      border-color: ${palette.blue.light2};
      color: ${palette.blue.dark1};
    }
    &.post {
      background-color: ${palette.green.light3};
      border-color: ${palette.green.light2};
      color: ${palette.green.dark2};
    }
    &.patch,
    &.put {
      background-color: ${palette.yellow.light3};
      border-color: ${palette.yellow.light2};
      color: ${palette.yellow.dark2};
    }
    &.delete {
      background-color: ${palette.red.light3};
      border-color: ${palette.red.light2};
      color: ${palette.red.dark2};
    }
  }
`;

export const deprecatedBadgeCss = css`
  // "deprecated" badge; related to the warning color theme
  span[type='warning'] {
    border-radius: 4px;
  }
`;
